import { Box, Button, Chip, Paper, Typography } from "@material-ui/core";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Email } from "@material-ui/icons";
import MediaLibraryModal from "../../../Common/MediaLibrary/MediaLibraryModal";
import SignableDocumentsFullScreenEditor from "./SignableDocumentsFullScreenEditor";
import useSignableDocumentsEditorStyles from "./hooks/useSignableDocumentsEditorStyles";
import { setupSignableDocumentsMappableFieldsHighlighting } from "../MessageTemplates/utils/aceEditorUtils";
import { ViewMode } from "./utils/constants";
import SignableDocumentsEditorTabs from "./components/SignableDocumentsEditorTabs";
import SignableDocumentsEditorBody from "./components/SignableDocumentsEditorBody";
import { MappableFieldsInfo } from "./components/MappableFieldsInfo";
import { SIGNABLE_DOCUMENTS_MAPPABLE_FIELDS_CONFIG } from "../MessageTemplates/utils/constants";
import {
  sanitizeAndFormatHTML,
  sanitizeHTML,
} from "../../../../utils/htmlHelpers";

const SignableDocumentsEditor = ({
  value,
  onChange,
  onBlur,
  resources,
  disabled,
  dealerGroupId,
  markPreviewAsViewed,
  showPreview,
  documentContext,
}) => {
  const classes = useSignableDocumentsEditorStyles();
  const [viewMode, setViewMode] = useState(ViewMode.EDIT);
  const [fullScreenOpen, setFullScreenOpen] = useState(false);
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);
  const editorRef = useRef(null);
  const highlighterRef = useRef(null);

  useEffect(() => {
    if(showPreview && viewMode !== ViewMode.PREVIEW) {
      setViewMode(ViewMode.PREVIEW)
    }
  }, [showPreview])

  const [editorReady, setEditorReady] = useState(false);

  useEffect(() => {
    if (!editorReady || !editorRef.current?.editor) {
      return;
    }

    if (highlighterRef.current) {
      highlighterRef.current.dispose();
      highlighterRef.current = null;
    }

    highlighterRef.current = setupSignableDocumentsMappableFieldsHighlighting(
      editorRef.current.editor,
      documentContext
    );

    return () => {
      if (highlighterRef.current) {
        highlighterRef.current.dispose();
        highlighterRef.current = null;
      }
    };
  }, [documentContext, editorReady]);

  const handleAceChange = useCallback((newValue) => {
    onChange(newValue);
  }, [onChange]);

  const handleTabChange = useCallback((event, newValue) => {
    if (newValue === ViewMode.PREVIEW) {
      setViewMode(ViewMode.PREVIEW);
      markPreviewAsViewed();
    }

    if (newValue === ViewMode.EDIT) {
      setViewMode(ViewMode.EDIT);
      setTimeout(() => {
        const editor = editorRef.current?.editor;
        if (editor) {
          editor.resize(true);
          editor.renderer.updateFull(true);
          editor.focus();
        }
      }, 0);
    }
  }, [markPreviewAsViewed]);

  const handleOpenFullScreen = useCallback(() => {
    setFullScreenOpen(true);
  }, []);

  const handleCloseFullScreen = useCallback(() => {
    setFullScreenOpen(false);
    if (onBlur) onBlur();
  }, [onBlur]);

  const handleOpenMediaLibrary = useCallback(() => {
    setMediaLibraryOpen(true);
  }, []);

  const handleCloseMediaLibrary = useCallback(() => {
    setMediaLibraryOpen(false);
  }, []);

  const handleBlur = useCallback(() => {
    if (onBlur && typeof onBlur === "function") {
      onBlur();
    }
  }, [onBlur]);

  const handleEditorLoad = useCallback(() => {
    setEditorReady(true);
  }, []);

  useEffect(() => {
    const style = document.createElement("style");
    style.id = "hide-ace-readonly-tooltip";
    style.textContent = `
			.ace_tooltip {
				display: none !important;
			}
		`;

    if (!document.getElementById("hide-ace-readonly-tooltip")) {
      document.head.appendChild(style);
    }

    return () => {
      const existingStyle = document.getElementById(
        "hide-ace-readonly-tooltip"
      );

      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (highlighterRef.current) {
        highlighterRef.current.dispose();
        highlighterRef.current = null;
      }
      const editor = editorRef.current?.editor;
      if (editor) {
        editor.setOptions({
          enableBasicAutocompletion: false,
          enableLiveAutocompletion: false,
        });
        editor.completers = [];
      }
    };
  }, []);

  function hasHTMLContent() {
    return sanitizeHTML(value).replace(/<\/?(html|head|body)[^>]*>/gi, "").replace(/\s+/g, "");
  }

  return (
    <Box className={classes.editorContainer}>
      <Box className={classes.header}>
        <Typography variant="subtitle1" className={classes.headerLabel}>
          {resources["DocumentContent"]}
          <span style={{ color: "#f44336", marginLeft: "4px" }}>*</span>
        </Typography>

        <Chip
          size="small"
          icon={<Email fontSize="small" />}
          label="HTML"
          color="primary"
        />
      </Box>

      <Box>
        <SignableDocumentsEditorTabs
          resources={resources}
          viewMode={viewMode}
          handleTabChange={handleTabChange}
          disabled={disabled}
          handleOpenMediaLibrary={handleOpenMediaLibrary}
          handleOpenFullScreen={handleOpenFullScreen}
        />

        <SignableDocumentsEditorBody
          value={value}
          handleAceChange={handleAceChange}
          handleBlur={handleBlur}
          viewMode={viewMode}
          disabled={disabled}
          editorRef={editorRef}
          onEditorReady={handleEditorLoad}
        />

        {viewMode === ViewMode.PREVIEW && (
          <Paper className={classes.previewContainer}>
            <iframe
              srcDoc={ hasHTMLContent() ? sanitizeHTML(value) : `<p>${resources["NoContentToPreview"]}</p>` }
              style={{
                width: "100%",
                minHeight: "400px",
                height: "auto",
                border: "none",
              }}
              title="HTML Preview"
            />
          </Paper>
        )}

        {fullScreenOpen && (
          <SignableDocumentsFullScreenEditor
            open={fullScreenOpen}
            onClose={handleCloseFullScreen}
            value={value}
            onChange={handleAceChange}
            onSave={handleCloseFullScreen}
            resources={resources}
            dealerGroupId={dealerGroupId}
            markPreviewAsViewed={markPreviewAsViewed}
            showPreview={showPreview}
            documentContext={documentContext}
          />
        )}
      </Box>

      {viewMode === 0 && (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <MappableFieldsInfo
            availableFields={SIGNABLE_DOCUMENTS_MAPPABLE_FIELDS_CONFIG[documentContext] || []}
            resources={resources}
            chipClass={classes.chip}
          />
          <Button
            id="signable-documents-format-html"
            style={{ marginTop: "8px" }}
            variant="outlined"
            onClick={() => onChange(sanitizeAndFormatHTML(value))}
          >
            {resources["FormatHTML"]}
          </Button>
        </div>
      )}
      {mediaLibraryOpen && (
        <MediaLibraryModal
          open={mediaLibraryOpen}
          onClose={handleCloseMediaLibrary}
          module="signableDocuments"
          resources={resources}
          dealerGroupId={dealerGroupId}
        />
      )}
    </Box>
  );
};

export default SignableDocumentsEditor;
